import { type Dispatch, type FC, type ReactElement, type SetStateAction, useCallback, useMemo, useState } from "react";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { fieldIsInvalid } from "../../../app-utils/form.js";
import { charsLeftMessage } from "../../../app-utils/string.js";
import { type Attribute } from "../../../graphql.js";
import ScriptInputField, { type IScriptingInputFieldProps, scriptAttributeRegex } from "../forms/ScriptInputField.js";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputHelp } from "@/ui-modules/core/form/layout/FormInputHelp.js";
import { FormInputHint } from "@/ui-modules/core/form/layout/FormInputHint.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";

export interface IScriptInputFormFieldProps extends IScriptingInputFieldProps, Partial<WrappedFieldProps> {
  name: string;
  fieldHint?: string;
  maxLength?: number;
  className?: string;
  title?: string | ReactElement;
  tooltip?: string;
  horizontalLayout?: boolean;
  setSyntaxError: Dispatch<SetStateAction<string>>;
  isEmbeddedAttributeValid?: (attributeId: number) => string; // useful to let the parent provide an error message when the embedded attribute id is not valid for some reason
}

const ScriptInputFormField: FC<IScriptInputFormFieldProps> = (props) => {
  const message: string = props.maxLength ? charsLeftMessage(props.maxLength, props.input?.value.length, 50) : null;

  const { input, disabled, isLoading, attributes, scriptingLanguage, acceptedMacros } = { ...props };

  const fieldHint =
    props.fieldHint || (attributes ? `Embed attributes ${acceptedMacros ? "and macros " : ""}using Ctrl+Space` : null);
  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.title && <FormLabel>{props.title}</FormLabel>}
      <FormInputWidget tooltip={props.tooltip}>
        <FormInputHint align={"end"}>{message}</FormInputHint>
        <ScriptInputField
          scriptingLanguage={scriptingLanguage}
          attributes={attributes}
          disabled={disabled}
          isLoading={isLoading}
          initialValue={input?.value}
          onChangeScript={input?.onChange}
          acceptedMacros={acceptedMacros}
          setSyntaxError={props.setSyntaxError}
        />
        {fieldHint && <FormInputHelp>{fieldHint}</FormInputHelp>}
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}
      </FormInputWidget>
    </FormInputGroup>
  );
};

export const validateScriptAttributes = (
  script: string,
  validate?: (attributeId: number) => string,
  attributes?: Attribute[],
) => {
  if (!script || !attributes) {
    return;
  }
  const attributesIdMatching = [...script.matchAll(scriptAttributeRegex)];
  if (attributesIdMatching.length) {
    const errorMessages = attributesIdMatching
      .map((attributesIMatching) => {
        const attributeId = Number(attributesIMatching[1]);

        if (validate) {
          return validate(attributeId);
        } else {
          if (!attributes.find((attribute) => attribute.id == attributeId)) {
            return `Attribute '${attributeId}' is unknown or not of a compatible type`;
          }
        }
      })
      .filter((errorMessage: string) => !!errorMessage);

    if (errorMessages.length) {
      return errorMessages[0];
    }
  }
  return;
};

export function ScriptInputReduxFormField(
  props: IScriptInputFormFieldProps & BaseFieldProps<IScriptInputFormFieldProps>,
) {
  const attributesValidator = useCallback(
    (value) => validateScriptAttributes(value, props.isEmbeddedAttributeValid, props.attributes),
    [props.isLoading], // the list of attributes might be computed on the fly, always changing reference
  );

  const [syntaxError, setSyntaxError] = useState<string>();
  const checkErrors = useCallback(() => syntaxError, [syntaxError]);

  const reduxValidators = useMemo(() => {
    const validators = Array.isArray(props.validate) ? props.validate : props.validate ? [props.validate] : [];
    validators.push(attributesValidator, checkErrors);
    return validators;
  }, [attributesValidator, checkErrors]);

  return (
    <Field {...props} component={ScriptInputFormField} validate={reduxValidators} setSyntaxError={setSyntaxError} />
  );
}
